import { useMemo, useState } from 'react';
import { Button, Checkbox, Pagination, SearchBar } from '@ab-inbev-maz/compass';
import type { IconName } from '@ab-inbev-maz/compass/dist/entities/components/icon.js';
import { showGlobalModal } from '../../stores';
import EmptyTable from './EmptyTable';
import GridCard from './GridCard';

export interface GridCardItem {
  id: string | number;
  title: string;
  subtitle?: string;
  thumbnailUrl: string;
  disabled?: boolean;
}

interface TableGridViewProps {
  title?: string;
  datos: GridCardItem[];
  currentPage?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  showDeleteButton?: boolean;
  showDownloadButton?: boolean;
  onDelete?: (selectedIds: (string | number)[]) => void;
  onDownload?: (selectedIds: (string | number)[]) => void;
  onOpenItem?: (id: string | number) => void;
  emptyMessage?: string;
  emptyDescription?: React.ReactNode;
  emptyIcon?: IconName;
  deleteConfirmTitle?: string;
  deleteConfirmMessage?: string;
}

// Copy of Tableview adapted to a card grid instead of table rows.
const TableGridView = ({
  title,
  datos = [],
  currentPage = 1,
  pageSize = 25,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  searchValue = '',
  onSearchChange,
  showDeleteButton = true,
  showDownloadButton = false,
  onDelete,
  onDownload,
  onOpenItem,
  emptyMessage = 'No hay elementos disponibles',
  emptyDescription,
  emptyIcon = 'validation/alert-octagon',
  deleteConfirmTitle = '¿Estás seguro de eliminar los elementos seleccionados?',
  deleteConfirmMessage = 'Una vez eliminados, no podrás recuperarlos ni acceder a ellos nuevamente.',
}: TableGridViewProps) => {
  const [selectedItems, setSelectedItems] = useState<Set<string | number>>(new Set());

  const enabledIds = useMemo(
    () => datos.filter((item) => !item.disabled).map((item) => item.id),
    [datos]
  );

  const handleCheckItem = (checked: boolean, id: string | number) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const handleDeleteClick = () => {
    showGlobalModal({
      title: deleteConfirmTitle,
      message: deleteConfirmMessage,
      iconType: 'warning',
      primaryAction: {
        label: 'Eliminar',
        onClick: () => {
          onDelete?.(Array.from(selectedItems));
          setSelectedItems(new Set());
        },
      },
      secondaryAction: {
        label: 'Cancelar',
        onClick: () => {},
      },
    });
  };

  return (
    <section className="flex items-center justify-between p-4 gap-6">
      <div className="w-full border border-gray-200 rounded-xl bg-white">
        <div className="flex items-center justify-between gap-8 py-2 px-6 rounded-t-2xl bg-[#ebebeb] shadow-[0_2px_8px_0_rgba(32,32,32,0.08)]">
          <h2 className="text-base font-bold uppercase text-[#141414]">{title}</h2>

          <div className="flex items-center gap-4">
            <SearchBar
              id="table-grid-search-bar"
              name="Buscar"
              variant="default"
              leftIcon="search"
              className="w-full min-w-[320px] rounded-md"
              placeholder="Buscar..."
              value={searchValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange?.(e.target.value)}
              onClear={() => onSearchChange?.('')}
            />
            {showDeleteButton && (
              <Button
                label="Eliminar"
                rightIcon="system/trash"
                variant="secondary"
                className="cursor-pointer"
                disabled={selectedItems.size === 0}
                onClick={handleDeleteClick}
              />
            )}
            {showDownloadButton && (
              <Button
                label="Descargar"
                rightIcon="arrow/download"
                variant="primary"
                className="cursor-pointer"
                disabled={selectedItems.size === 0}
                onClick={() => onDownload?.(Array.from(selectedItems))}
              />
            )}
          </div>
        </div>

        <div className="p-4">
          {datos.length === 0 ? (
            <EmptyTable message={emptyMessage} description={emptyDescription} icon={emptyIcon} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7 gap-6">
              {datos.map((item) => (
                <div key={item.id} className="relative">
                  <Checkbox
                    id={`grid-item-check-${item.id}`}
                    checked={selectedItems.has(item.id)}
                    disabled={item.disabled || !enabledIds.includes(item.id)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckItem(e.target.checked, item.id)}
                    className="absolute top-2 left-2 z-10 bg-white rounded"
                  />
                  <GridCard
                    imageSrc={item.thumbnailUrl}
                    imageAlt={item.title}
                    title={item.title}
                    description={item.subtitle}
                    disabled={item.disabled}
                    onClick={() => onOpenItem?.(item.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {datos.length > 0 && (
          <div className="flex items-center justify-between gap-4 p-4 border-t border-gray-200">
            <Pagination
              className="flex-row! flex-nowrap!"
              page={currentPage}
              total={totalCount}
              size={pageSize}
              sizes={[
                { label: 'Filas por página: 25', value: 25 },
                { label: 'Filas por página: 50', value: 50 },
                { label: 'Filas por página: 100', value: 100 },
              ]}
              onChangePage={onPageChange}
              onChangeSize={onPageSizeChange}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default TableGridView;
